import { Box, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import strings from 'common/Translation/Translate';
import {
  calculateExpectedGuests,
  validateExpectedGuestsCapacity,
} from 'utils/validators/HelperFunctions';
import { Validators } from 'utils/validators/Validators';

type Props = {
  guestsLabel: string;
  expectedLabel: string;
  field: string;
  guestsValue: string | number;
  disabled?: boolean;
  hasError?: boolean;
  fieldError?: boolean;
  venueMaxCapacity?: number | null;
  venueName?: string | null;
  layoutMaxCapacity?: number | null;
  layoutName?: string | null;
  layoutSelected?: boolean;
  excessGuestsPercentage?: number | null;
  textChange: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  size?: 'small' | 'medium';
  requiredMessage?: string;
};

type ValidationResult = { error: boolean; message: string } | false;

const normalizeValidatorError = (
  result: false | { error: boolean; message: string | (string | number)[] },
): ValidationResult => {
  if (!result) return false;
  const message = Array.isArray(result.message) ? result.message.map(String).join('') : String(result.message);
  return { error: result.error, message };
};

const isGuestsEmpty = (value: unknown): boolean => value == null || String(value).trim() === '';

/** Validates guaranteed guest count only (required + integer >= 0). */
const validateGuaranteedGuestsOnly = (
  value: string,
  includeRequired: boolean,
): ValidationResult => {
  if (isGuestsEmpty(value)) {
    if (includeRequired) {
      return { error: true, message: strings.requiredText };
    }
    return false;
  }

  const intError = Validators.validateInt(value, strings.common_number_validation_error, 0, 100000);
  if (intError) return normalizeValidatorError(intError);

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { error: true, message: strings.common_number_validation_error };
  }

  return false;
};

/** Full validation for form commit (venue capacity, then layout when selected). */
const validateGuestCountForCommit = (
  value: string,
  excessGuestsPercentage: number | null | undefined,
  venueMaxCapacity: number | null | undefined,
  venueName: string | null | undefined,
  layoutMaxCapacity: number | null | undefined,
  layoutName: string | null | undefined,
  layoutSelected: boolean,
): ValidationResult => {
  const guaranteedError = validateGuaranteedGuestsOnly(value, true);
  if (guaranteedError) return guaranteedError;

  return validateExpectedGuestsCapacity(
    value,
    excessGuestsPercentage,
    venueMaxCapacity,
    venueName,
    layoutMaxCapacity,
    layoutName,
    layoutSelected,
  );
};

const GuestCountSplitField: FC<Props> = ({
  guestsLabel,
  expectedLabel,
  field,
  guestsValue,
  disabled,
  hasError,
  fieldError,
  venueMaxCapacity,
  venueName,
  layoutMaxCapacity,
  layoutName,
  layoutSelected = false,
  excessGuestsPercentage,
  textChange,
  size = 'small',
  requiredMessage,
}) => {
  const guestsStr = guestsValue === 0 || guestsValue ? String(guestsValue) : '';
  const expectedGuests = calculateExpectedGuests(guestsStr, excessGuestsPercentage);

  const guaranteedFieldError = useMemo((): ValidationResult => {
    if (isGuestsEmpty(guestsStr)) {
      return hasError && fieldError
        ? { error: true, message: requiredMessage || strings.requiredText }
        : false;
    }
    return validateGuaranteedGuestsOnly(guestsStr, false);
  }, [guestsStr, hasError, fieldError, requiredMessage]);

  const expectedFieldError = useMemo((): ValidationResult => {
    if (isGuestsEmpty(guestsStr)) return false;
    if (guaranteedFieldError) return false;
    return validateExpectedGuestsCapacity(
      guestsStr,
      excessGuestsPercentage,
      venueMaxCapacity,
      venueName,
      layoutMaxCapacity,
      layoutName,
      layoutSelected,
    );
  }, [
    guestsStr,
    guaranteedFieldError,
    excessGuestsPercentage,
    venueMaxCapacity,
    venueName,
    layoutMaxCapacity,
    layoutName,
    layoutSelected,
  ]);

  const hasGuaranteedError = guaranteedFieldError !== false && guaranteedFieldError.error;
  const guaranteedMessage = guaranteedFieldError !== false ? guaranteedFieldError.message : '';

  const hasExpectedError = expectedFieldError !== false && expectedFieldError.error;
  const expectedMessage = expectedFieldError !== false ? expectedFieldError.message : '';

  const handleGuestsChange = useCallback(
    (value: string) => {
      if (value !== '' && Number.isNaN(Number(value))) return;
      const inputError = validateGuestCountForCommit(
        value,
        excessGuestsPercentage,
        venueMaxCapacity,
        venueName,
        layoutMaxCapacity,
        layoutName,
        layoutSelected,
      );
      textChange(value, field, inputError || undefined);
    },
    [
      field,
      excessGuestsPercentage,
      venueMaxCapacity,
      venueName,
      layoutMaxCapacity,
      layoutName,
      layoutSelected,
      textChange,
    ],
  );

  useEffect(() => {
    if (isGuestsEmpty(guestsStr)) return;
    const inputError = validateGuestCountForCommit(
      guestsStr,
      excessGuestsPercentage,
      venueMaxCapacity,
      venueName,
      layoutMaxCapacity,
      layoutName,
      layoutSelected,
    );
    textChange(guestsStr, field, inputError || undefined);
    // Re-sync when capacity context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueMaxCapacity, venueName, layoutMaxCapacity, layoutName, layoutSelected, excessGuestsPercentage]);

  return (
    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
      <TextField
        size={size}
        fullWidth
        label={`${guestsLabel} *`}
        value={guestsStr}
        disabled={disabled}
        onChange={e => handleGuestsChange(e.target.value)}
        error={hasGuaranteedError}
        helperText={hasGuaranteedError ? guaranteedMessage : ' '}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
      />
      <TextField
        size={size}
        fullWidth
        label={expectedLabel}
        value={guestsStr === '' ? '' : String(expectedGuests)}
        disabled
        InputProps={{ readOnly: true }}
        error={hasExpectedError}
        helperText={hasExpectedError ? expectedMessage : ' '}
      />
    </Box>
  );
};

export default GuestCountSplitField;
